import { AlertTriangle, CheckCircle2, Clock3, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useBillingStatus, isBillingFrontendEnabled } from '../hooks/useBilling';
import { useBillingGate } from '../hooks/useBillingGate';
import { getTrialDaysRemaining, resolveBillingLevel } from '../utils/billingStatus';

export const BillingStatusBanner = () => {
  const { t } = useTranslation('common');
  const { lng = 'es' } = useParams();
  const { data } = useBillingStatus();
  const { isReadOnly } = useBillingGate();

  if (!isBillingFrontendEnabled()) {
    return null;
  }

  // When writes are gated, the dedicated read-only paywall banner takes over —
  // don't stack a second billing CTA on top of it.
  if (isReadOnly) {
    return null;
  }

  if (!data?.billing_enabled) {
    return (
      <div className="rounded-lg border border-line bg-white px-4 py-3 text-sm text-muted">
        {t('billing.banner.disabled')}
      </div>
    );
  }

  const level = resolveBillingLevel(data);
  const billingHref = `/${lng}/billing`;

  if (level === 'ACTIVE') {
    // At-the-corriente subscriptions get no banner (per billing contract).
    return null;
  }

  if (level === 'TRIAL') {
    const days = getTrialDaysRemaining(data.subscription?.trial_end);
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        <span>
          {days !== null
            ? t('billing.banner.trialDays', { count: days })
            : t('billing.banner.trialing')}
        </span>
      </div>
    );
  }

  if (level === 'NONE') {
    return (
      <Link
        to={billingHref}
        className="flex items-center gap-2 rounded-lg border border-brand-100 bg-brand-50 px-4 py-3 text-sm text-brand-700"
      >
        <CreditCard className="h-4 w-4" aria-hidden="true" />
        <span>{t('billing.banner.none')}</span>
      </Link>
    );
  }

  if (level === 'PAST_DUE') {
    return (
      <Link
        to={billingHref}
        className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
      >
        <Clock3 className="h-4 w-4" aria-hidden="true" />
        <span>{t('billing.banner.pastDue')}</span>
      </Link>
    );
  }

  // CANCELED
  return (
    <Link
      to={billingHref}
      className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
    >
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <span>{t('billing.banner.canceled')}</span>
    </Link>
  );
};
